import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtCurrency, fmtDateTime } from "@/lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { ExportButton } from "@/components/ExportButton";
import { CircleDot, PlayCircle, CheckCircle2, CircleCheckBig, Ticket, PhoneCall, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CALL_TYPE_LABELS } from "@/pages/Tickets";

// "Segregated" report tabs — mirror the ticket status pipeline
const TABS = [
  { key: "pending",     label: "Pending",   status: "open",        icon: CircleDot,    tint: "text-slate-500" },
  { key: "assigned",    label: "Assigned",  status: "in_progress", icon: PlayCircle,   tint: "text-blue-500" },
  { key: "completed",   label: "Completed", status: "resolved",    icon: CheckCircle2, tint: "text-emerald-500" },
  { key: "closed",      label: "Closed",    status: "closed",      icon: CircleCheckBig, tint: "text-purple-500" },
];

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [perf, setPerf] = useState([]);
  const [callTypes, setCallTypes] = useState([]);
  const [callTypeDays, setCallTypeDays] = useState(0);
  const [branches, setBranches] = useState([]);
  const [branchDays, setBranchDays] = useState(0);
  const [tab, setTab] = useState("pending");
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([api.get("/dashboard/summary"), api.get("/dashboard/engineer-performance")]);
        setSummary(s.data); setPerf(p.data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    const active = TABS.find((t) => t.key === tab);
    if (!active) return;
    setBusy(true);
    api.get(`/tickets?status=${active.status}`)
      .then((r) => setTickets(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setBusy(false));
  }, [tab]);

  useEffect(() => {
    api.get(`/tickets/analytics/call-types${callTypeDays ? `?days=${callTypeDays}` : ""}`)
      .then((r) => setCallTypes(
        (r.data.rows || []).map((x) => ({
          ...x,
          label: x.call_type === "unspecified" ? "Not Set" : (CALL_TYPE_LABELS[x.call_type] || x.call_type),
        }))
      ))
      .catch((e) => setErr(formatApiError(e)));
  }, [callTypeDays]);

  useEffect(() => {
    api.get(`/tickets/analytics/branches${branchDays ? `?days=${branchDays}` : ""}`)
      .then((r) => setBranches(
        (r.data.rows || []).map((x) => ({
          ...x,
          label: x.branch === "unspecified" ? "No Branch Set" : x.branch,
        }))
      ))
      .catch((e) => setErr(formatApiError(e)));
  }, [branchDays]);

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = (summary?.tickets_by_status || {})[t.status] || 0;
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-primary">Analytics</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">Operational insight across the entire service pipeline.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportButton resource="tickets" testid="reports-export-tickets" />
          <ExportButton resource="invoices" testid="reports-export-invoices" />
          <ExportButton resource="engineer-performance" testid="reports-export-perf" />
          <ExportButton resource="amc" testid="reports-export-amc" />
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {/* KPI tiles */}
      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`report-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`sharp-card p-4 text-left transition-colors ${tab === t.key ? "border-primary ring-1 ring-primary/40" : "hover:border-primary/40"}`}
            >
              <div className={`flex h-9 w-9 items-center justify-center rounded-md bg-secondary ${t.tint}`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="label-eyebrow mt-3">{t.label} tickets</p>
              <p className="mt-1 font-heading text-3xl font-bold">{counts[t.key] ?? 0}</p>
            </button>
          );
        })}
      </section>

      {/* Ticket list by status */}
      <section className="sharp-card mb-6 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <Ticket className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-base font-semibold">
              {TABS.find((t) => t.key === tab)?.label} tickets
            </h2>
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] tabular-nums text-foreground/70">{tickets.length}</span>
          </div>
          {busy && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30 text-left">
              <tr>
                <th className="px-4 py-2 label-eyebrow">Ticket</th>
                <th className="px-4 py-2 label-eyebrow">Customer</th>
                <th className="px-4 py-2 label-eyebrow">Complaint</th>
                <th className="px-4 py-2 label-eyebrow">Engineer</th>
                <th className="px-4 py-2 label-eyebrow">Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id}
                  onClick={() => navigate(`/app/tickets/${t.id}`)}
                  data-testid={`report-ticket-${t.ticket_no}`}
                  className="cursor-pointer border-t border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2 font-mono text-xs">{t.ticket_no}</td>
                  <td className="px-4 py-2">{t.customer?.company_name || "—"}</td>
                  <td className="px-4 py-2 max-w-md truncate">{t.complaint}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{t.engineer?.name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                </tr>
              ))}
              {!busy && tickets.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-muted-foreground">No tickets in this bucket.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* KPI + engineer performance */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="sharp-card p-5">
          <p className="label-eyebrow">Open tickets</p>
          <p className="mt-1 font-heading text-3xl font-bold">{summary?.open_tickets ?? "—"}</p>
        </div>
        <div className="sharp-card p-5">
          <p className="label-eyebrow">Revenue this month</p>
          <p className="mt-1 font-heading text-3xl font-bold">{fmtCurrency(summary?.revenue_this_month || 0)}</p>
        </div>
        <div className="sharp-card p-5">
          <p className="label-eyebrow">Overdue AMCs</p>
          <p className="mt-1 font-heading text-3xl font-bold">{summary?.expiring_amcs ?? "—"}</p>
        </div>
      </section>

      <section className="mt-6 sharp-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Call volume by call type</h2>
          </div>
          <div className="flex gap-2">
            {[{ v: 0, l: "All time" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }].map((o) => (
              <button key={o.v} data-testid={`calltype-range-${o.v}`} onClick={() => setCallTypeDays(o.v)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${callTypeDays === o.v ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        {callTypes.length === 0 ? <p className="text-sm text-muted-foreground">No tickets yet.</p> : (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="h-64 w-full lg:col-span-2">
              <ResponsiveContainer>
                <BarChart data={callTypes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))">
                    {callTypes.map((_, i) => <Cell key={i} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {callTypes.map((c) => (
                <div key={c.call_type} data-testid={`calltype-row-${c.call_type}`} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <span className="text-sm">{c.label}</span>
                  <span className="font-heading text-sm font-bold">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="mt-6 sharp-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-semibold">Call volume by branch</h2>
          </div>
          <div className="flex gap-2">
            {[{ v: 0, l: "All time" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }].map((o) => (
              <button key={o.v} data-testid={`branch-range-${o.v}`} onClick={() => setBranchDays(o.v)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${branchDays === o.v ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        {branches.length === 0 ? <p className="text-sm text-muted-foreground">No tickets yet.</p> : (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="h-64 w-full lg:col-span-2">
              <ResponsiveContainer>
                <BarChart data={branches}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))">
                    {branches.map((_, i) => <Cell key={i} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {branches.map((b) => (
                <div key={b.branch} data-testid={`branch-row-${b.branch}`} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <span className="text-sm">{b.label}</span>
                  <span className="font-heading text-sm font-bold">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="mt-6 sharp-card p-5">
        <h2 className="mb-4 font-heading text-lg font-semibold">Engineer performance</h2>
        {perf.length === 0 ? <p className="text-sm text-muted-foreground">No engineer data yet.</p> : (
          <div className="h-64 w-full">
            <ResponsiveContainer>
              <BarChart data={perf}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="completed" fill="hsl(var(--primary))">
                  {perf.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </div>
  );
}
